// src/components/layout/FullBleedLayout.js
//
// Full-bleed shell for pages that need to break out of the app's centered,
// fixed-width container — the super-admin DRAWING pages (Cabinet / Item / Room
// / Image Library) and the order Drawings workspace. This wrapper does it on
// its own, using the left-1/2 + -50vw technique, so it needs no changes to
// App.js or the routing shell to work.
//
// ---------------------------------------------------------------------------
// HOW THE NAVBAR STAYS IN SYNC
// ---------------------------------------------------------------------------
// The Navbar needs to go full-width on exactly the same screens this wrapper
// is used on. That USED to be done by matching the URL against a fixed list
// (DRAWING_ROUTE_PREFIXES, below) — fine when a full-bleed page has a route of
// its own, like /super-admin/room-drawings.
//
// It breaks down for the order Drawings workspace. /order/:orderId is shared
// by BOTH a drawing-mode order (full-bleed) and a pricing-mode order
// (OrderManagement, centered) — OrderWorkspaceRouter forks between them based
// on the order's data, not the URL. There is no path the Navbar could add to
// the list that means "this order, when it turns out to be a drawing order."
//
// So the source of truth is now the component itself: every time a
// FullBleedLayout mounts or unmounts, it broadcasts that fact, and the Navbar
// (Navbar.js) subscribes via subscribeFullBleed(). Whichever screen is
// actually rendering full-bleed is, by construction, the one the Navbar should
// match — no route list to keep in lockstep, and no guessing at order mode.
//
// DRAWING_ROUTE_PREFIXES / isDrawingRoute() are kept for any route that wants
// a same-tick, render-time answer (before the mount effect below has had a
// chance to fire) or for callers outside this component tree. The Navbar ORs
// both signals together, so either one lighting up is enough.
// ---------------------------------------------------------------------------

import React, { useLayoutEffect } from 'react';

// Routes that are ALWAYS full-bleed, independent of any component state. Keep
// this to pages that are full-bleed under every circumstance — the order
// workspace is deliberately NOT here, because whether it is full-bleed depends
// on the order's mode, which this list cannot express. See the header above.
export const DRAWING_ROUTE_PREFIXES = [
  '/super-admin/room-drawings',
  '/super-admin/item-drawings',
];

// True when the given pathname is one of the always-full-bleed routes (exact
// match, or a nested path under it like /super-admin/room-drawings/42).
export function isDrawingRoute(pathname = '') {
  return DRAWING_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
}

// ---------------------------------------------------------------------------
// MOUNT-BASED SIGNAL
// ---------------------------------------------------------------------------
// A tiny window-event pub/sub, not React context, specifically so the Navbar
// needs no Provider wired up above it in App.js. Any FullBleedLayout anywhere
// in the tree can announce itself to any subscriber anywhere else.
//
// COUNTER, not a boolean: if two FullBleedLayout instances were ever on screen
// at once (nested, or a transition where the outgoing page hasn't unmounted
// before the incoming one mounts), a naive boolean would have the first one's
// unmount turn the signal off while the second is still active. The counter
// only reports "off" once nothing is mounted.
const FULL_BLEED_EVENT = 'quickbid:fullbleed-change';
let activeFullBleedCount = 0;

function notifyFullBleedChange() {
  window.dispatchEvent(new CustomEvent(FULL_BLEED_EVENT, {
    detail: { active: activeFullBleedCount > 0 },
  }));
}

// Subscribe to full-bleed state. Calls `callback(active)` immediately with the
// CURRENT state (so a subscriber mounting after the page is already full-bleed
// — e.g. Navbar surviving a hot reload — doesn't start out wrong), and again
// every time it changes. Returns an unsubscribe function.
export function subscribeFullBleed(callback) {
  const handler = (e) => callback(e.detail.active);
  window.addEventListener(FULL_BLEED_EVENT, handler);
  callback(activeFullBleedCount > 0);
  return () => window.removeEventListener(FULL_BLEED_EVENT, handler);
}

/**
 * FullBleedLayout
 *
 * Wrap a page's content in this to make it span the full viewport width
 * regardless of the App shell's content container. Also registers this screen
 * as full-bleed for as long as it is mounted — see the header above.
 *
 * Props:
 *  - fullHeight (default true): fill the screen below the 4rem navbar.
 *  - pad (default true): apply small responsive side padding. Pass false for a
 *    page that wants true edge-to-edge (e.g. a canvas that paints to the edge).
 *  - className: extra classes merged onto the wrapper.
 *
 * The -50vw margins pull the element out of a centered parent; w-screen +
 * max-w-[100vw] span the viewport. (If a vertical scrollbar ever produces a
 * sliver of horizontal scroll, add `overflow-x-hidden` to the page body — the
 * 100vw width counts the scrollbar. Noted so it isn't a surprise later.)
 */
export default function FullBleedLayout({
  children,
  className = '',
  fullHeight = true,
  pad = true,
}) {
  // useLayoutEffect, not useEffect: the Navbar's own subscription also runs in
  // a layout effect, so the width class is decided before the browser paints —
  // otherwise the Navbar would render centered for one frame, then snap wide.
  useLayoutEffect(() => {
    activeFullBleedCount += 1;
    notifyFullBleedChange();
    return () => {
      activeFullBleedCount -= 1;
      notifyFullBleedChange();
    };
  }, []);

  const classes = [
    'relative left-1/2 right-1/2 w-screen max-w-[100vw]',
    '-ml-[50vw] -mr-[50vw] box-border',
    fullHeight ? 'min-h-[calc(100vh-4rem)]' : '',
    pad ? 'px-3 sm:px-4 lg:px-6' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <div className={classes}>{children}</div>;
}